import React, { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { View, type ViewStyle } from 'react-native';
import { Toast, type ToastProps } from './Toast';
import { tokens } from '../../design-system/tokens';

export interface ToastItem {
  id: string;
  variant?: ToastProps['variant'];
  message: string;
  duration?: number;
}

interface ToastContextValue {
  show: (toast: Omit<ToastItem, 'id'>) => void;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export const useToast = (): ToastContextValue => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};

export interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const show = useCallback((toast: Omit<ToastItem, 'id'>) => {
    const id = `toast-${Date.now()}-${Math.random()}`;
    setToasts((prev) => [...prev, { ...toast, id }]);
  }, []);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const value = { show, dismiss };

  return (
    <ToastContext.Provider value={value}>
      {children}
      {toasts.length > 0 && (
        <View style={styles.container} pointerEvents="box-none">
          {toasts.map((toast) => (
            <Toast
              key={toast.id}
              id={toast.id}
              variant={toast.variant ?? 'info'}
              message={toast.message}
              duration={toast.duration ?? 3000}
              onDismiss={dismiss}
            />
          ))}
        </View>
      )}
    </ToastContext.Provider>
  );
};

const styles = {
  container: {
    position: 'absolute',
    top: tokens.spacing.xl,
    left: tokens.spacing.md,
    right: tokens.spacing.md,
    zIndex: 9999,
  } satisfies ViewStyle,
};
